import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, type RegenerateAffectedDto, type WhereUsedDto, type WhereUsedTarget } from '@pb/shared';
import { regenerateAffected } from '../api/whereUsed';
import { useProject } from '../lib/project';
import type { Resource } from '../lib/useResource';
import { Dialog } from './Dialog';
import { RegenerateResults } from './Generations';
import { ErrorAlert, ErrorState, Loading } from './States';

export function WhereUsedList({ data, showGroups = true }: { data: WhereUsedDto; showGroups?: boolean }) {
  const { project } = useProject();
  const base = `/projects/${project.id}`;
  const nothing =
    (!showGroups || data.groups.length === 0) && data.presentations.length === 0 && data.events.length === 0;
  if (nothing) return <p className="small muted">Not used anywhere yet.</p>;
  return (
    <div className="stack small">
      {showGroups && data.groups.length ? (
        <div>
          <div className="field-label">Groups ({data.groups.length})</div>
          <ul className="list-plain" style={{ gap: 2 }}>
            {data.groups.map((g) => (
              <li key={g.id}>
                <Link to={`${base}/groups/${g.id}`}>{g.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {data.presentations.length ? (
        <div>
          <div className="field-label">Presentations ({data.presentations.length})</div>
          <ul className="list-plain" style={{ gap: 2 }}>
            {data.presentations.map((p) => (
              <li key={p.id}>
                <Link to={`${base}/presentations/${p.id}`}>{p.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {data.events.length ? (
        <div>
          <div className="field-label">Planned events ({data.events.length})</div>
          <ul className="list-plain" style={{ gap: 2 }}>
            {data.events.map((e) => (
              <li key={e.id}>
                <Link to={`${base}/events/${e.id}`}>{e.title}</Link> <span className="faint">{formatDate(e.date)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function RegenerateAffectedDialog({
  target,
  id,
  onClose,
}: {
  target: WhereUsedTarget;
  id: number;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<RegenerateAffectedDto | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await regenerateAffected(target, id));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Regenerate affected"
      wide
      onClose={onClose}
      closeDisabled={busy}
      footer={
        result ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void run()} disabled={busy}>
              {busy ? 'Regenerating…' : 'Regenerate affected'}
            </button>
          </>
        )
      }
    >
      {result ? (
        <RegenerateResults result={result} />
      ) : (
        <p>
          This builds a new file for every affected presentation that is not retired and refreshes the snapshots of
          affected planned events. Held events are never touched.
        </p>
      )}
      {busy ? <Loading label="Regenerating — this can take a little while…" /> : null}
      <ErrorAlert error={error} />
    </Dialog>
  );
}

export function WhereUsedPanel({
  target,
  id,
  resource,
  title = 'Where used',
}: {
  target: WhereUsedTarget;
  id: number;
  resource: Resource<WhereUsedDto>;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const data = resource.data;
  const affected = data ? data.presentations.length + data.events.length : 0;
  return (
    <section className="panel" aria-label={title}>
      <div className="panel-title">
        <h3>{title}</h3>
        <button type="button" className="btn btn-sm" onClick={() => setOpen(true)} disabled={!data || affected === 0}>
          Regenerate affected
        </button>
      </div>
      {resource.error ? (
        <ErrorState error={resource.error} onRetry={() => void resource.reload()} />
      ) : data ? (
        <WhereUsedList data={data} showGroups={target === 'slide'} />
      ) : (
        <Loading />
      )}
      {open ? <RegenerateAffectedDialog target={target} id={id} onClose={() => setOpen(false)} /> : null}
    </section>
  );
}

